import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Button from "../../components/Kids/Button";

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");
const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
const ARENA_BG = "#2A3A54";

function fmtElapsed(ms) {
  if (ms == null) return "—";
  const s = ms / 1000;
  return s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

// Landed on after a Race Battle game (e.g. a jigsaw puzzle) reports its own
// finish back to the multiplayer room. Polls the same room-state endpoint
// Quiz Battle uses, just rendered for the race player shape (finished/
// elapsed_ms) instead of quiz scores.
export default function KidsRaceResult() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = params.get("code");
  const childId = params.get("childId") || sessionStorage.getItem("childId");

  const [state, setState] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!code || !childId) return;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/kids/multiplayer/state`, { params: { code, child_id: childId }, ...auth() });
        setState(r.data.data);
        if (r.data.data.status === "complete") clearInterval(pollRef.current);
      } catch { clearInterval(pollRef.current); }
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [code, childId]);

  const goBack = () => navigate(`/kids/games?childId=${childId}`);

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ background: ARENA_BG }}>
        <div className="text-center">
          <div className="text-5xl mb-2 ar-pulse">🏁</div>
          <p className="font-display font-semibold text-base">Loading results...</p>
          <style>{`.ar-pulse { animation: pulse 2s ease-in-out infinite } @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }`}</style>
        </div>
      </div>
    );
  }

  const roster = [...(state.players || [])].sort((a, b) => {
    if (a.finished && b.finished) return a.elapsed_ms - b.elapsed_ms;
    if (a.finished) return -1;
    if (b.finished) return 1;
    return 0;
  });
  const me = state.players?.find(p => p.is_me);
  const winners = state.winners || [];
  const isComplete = state.status === "complete";
  const iWon = isComplete && me && winners.includes(me.name) && winners.length === 1;
  const tied = isComplete && winners.length > 1;
  const stillPlaying = roster.filter(p => !p.finished);

  const resultBg = !isComplete
    ? ARENA_BG
    : iWon
    ? "linear-gradient(160deg,#F5B31E,#FF8A4C)"
    : tied
    ? "linear-gradient(160deg,#9B7BFF,#6A47E0)"
    : ARENA_BG;

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: resultBg }}>
      {isComplete && iWon && (
        <>
          <style>{`@keyframes confetti { 0%{transform:translateY(-10vh) rotate(0)} 100%{transform:translateY(110vh) rotate(720deg)} }`}</style>
          {[...Array(18)].map((_, i) => (
            <div key={i} className="fixed top-0 text-2xl pointer-events-none"
              style={{ insetInlineStart: `${Math.random() * 100}%`, animation: `confetti ${2 + Math.random() * 2}s linear ${Math.random() * 2}s infinite` }}>
              {["⭐", "🏆", "🎉", "✨", "🥇"][Math.floor(Math.random() * 5)]}
            </div>
          ))}
        </>
      )}
      <div className="max-w-[380px] w-full text-center">
        {isComplete ? (
          <>
            <div className="text-8xl mb-2">{iWon ? "🏆" : tied ? "🤝" : "💪"}</div>
            <h1 className="font-display font-semibold text-[30px] text-white mb-1">
              {iWon ? t("kids.multiplayer.youWin") : tied ? t("kids.multiplayer.tie") : t("kids.multiplayer.youLose")}
            </h1>
          </>
        ) : (
          <>
            <div className="text-6xl mb-2 ar-pulse">🏁</div>
            <h1 className="font-display font-semibold text-2xl text-white mb-1">You finished!</h1>
          </>
        )}
        <p className="text-white/60 text-[13px] mb-5">{state.game_name}</p>

        {/* Race standings */}
        <div className="flex flex-col gap-2 mb-5">
          {roster.map((p, i) => {
            const isWinner = isComplete && winners.includes(p.name);
            return (
              <div key={p.child_id}
                className={`flex items-center justify-between bg-white/15 rounded-card px-4 py-3 border-2 ${isWinner ? "border-gold" : "border-transparent"} ${p.is_me ? "" : "bg-white/10"}`}>
                <div className="flex items-center gap-2 text-start">
                  <span className="font-display font-black text-white/50 text-sm w-4">{p.finished ? i + 1 : "—"}</span>
                  <span className="font-semibold text-white text-sm">
                    {p.is_me ? t("kids.multiplayer.you") : p.name}
                  </span>
                  {isWinner && <span className="text-[9px] font-extrabold text-gold">🏆</span>}
                </div>
                <div className="text-end">
                  {p.finished ? (
                    <div className="font-display font-black text-lg text-white">{fmtElapsed(p.elapsed_ms)}</div>
                  ) : (
                    <div className="text-[11px] text-white/50 ar-pulse">still playing…</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!isComplete && (
          <p className="text-[13px] text-white/50 mb-4">
            {stillPlaying.length > 0
              ? `Waiting for ${stillPlaying.map(p => p.name).join(", ")} to finish...`
              : "Waiting for results..."}
          </p>
        )}

        <Button variant="secondary" onClick={goBack} className="w-full">
          ← Back to Games
        </Button>
      </div>
      <style>{`.ar-pulse { animation: pulse 2s ease-in-out infinite } @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }`}</style>
    </div>
  );
}
